/**
 * useInventoryAgingData Hook
 *
 * Fetches the latest new-vehicle inventory-aging snapshot for the currently
 * selected location on the Forecast page (NEW vehicle department). Backs the
 * "Inventory" collapsible card.
 */

import { useState, useEffect, useCallback } from 'react';
import { getInventoryAging, InventoryAgingResponse } from '../services/api';

interface UseInventoryAgingDataParams {
  locationId: number;
  enabled: boolean;
}

interface UseInventoryAgingDataResult {
  data: InventoryAgingResponse | null;
  loading: boolean;
  error: string | null;
}

export function useInventoryAgingData({
  locationId,
  enabled,
}: UseInventoryAgingDataParams): UseInventoryAgingDataResult {
  const [data, setData] = useState<InventoryAgingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getInventoryAging(locationId);
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError(res.error || 'Failed to load inventory aging data');
        setData(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setData(null);
      setError(null);
      return;
    }
    fetchData();
  }, [locationId, enabled, fetchData]);

  return { data, loading, error };
}
